import { FC, useLayoutEffect, useState } from "react";
import Navigation from "./Navigation";

/**
 * The header's inline nav, shown only while it fits (1NOW-11061, QA reopen of 7 Oct).
 *
 * A host can now put every page in the header, and the nav used to be pinned
 * (`absolute left-1/2` on Design B's split layout, or just never shrunk), so ten
 * items slid under the logo and under Book Now. The nav now sits in a
 * `flex-1 min-w-0` slot between the logo and the right cluster. It is always
 * rendered, at its natural width (`w-max`), so it can be measured. If it is wider
 * than the slot it is made invisible and taken out of the flow, and the menu
 * button, which already lists every item, takes over. Nothing is added or hidden
 * for a menu that fits.
 *
 *   const fit = useNavFit("xl");
 *   {!showSearch && <NavSlot fit={fit} align="center" />}
 *   <div ref={fit.menuRef} className={showSearch ? "" : fit.menuClass}><MenuBar /></div>
 *
 * `from` is the breakpoint the inline nav starts at (Design A lg, Design B xl);
 * below it the menu button always shows, as before.
 */

type From = "lg" | "xl";
// Whole class names, so Tailwind can see them.
const NAV_FROM: Record<From, string> = { lg: "hidden lg:!flex", xl: "hidden xl:!flex" };
const MENU_HIDDEN_FROM: Record<From, string> = { lg: "lg:!hidden", xl: "xl:!hidden" };

/** The room the menu button takes in the right cluster while it is shown, gap or margin included. */
const menuSlot = (menu: HTMLElement | null) => {
  if (!menu || !menu.offsetWidth) return 0; // display:none: not shown, takes no room
  const gap = parseFloat(getComputedStyle(menu.parentElement as HTMLElement).columnGap) || 0;
  return menu.getBoundingClientRect().width + (parseFloat(getComputedStyle(menu).marginLeft) || 0) + gap;
};

export interface NavFit {
  fits: boolean;
  setArea: (el: HTMLElement | null) => void;
  setNav: (el: HTMLElement | null) => void;
  menuRef: (el: HTMLElement | null) => void;
  /** Where the inline nav is allowed to show. */
  navClass: string;
  /** For the menu button's wrapper: hidden from `from` up only while the nav fits. */
  menuClass: string;
}

export const useNavFit = (from: From): NavFit => {
  const [area, setArea] = useState<HTMLElement | null>(null);
  const [nav, setNav] = useState<HTMLElement | null>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [fits, setFits] = useState(true);

  useLayoutEffect(() => {
    if (!area || !nav) return;
    const measure = () => {
      // 0 while the nav is display:none (below `from`, or no layout at all): nothing to fit.
      const natural = nav.getBoundingClientRect().width;
      // The slot is narrower by the menu button while the button is shown. Count it back,
      // or hiding the nav (which shows the button) would shrink the slot and keep it hidden.
      const room = area.getBoundingClientRect().width + menuSlot(menu);
      setFits(natural === 0 || natural + 1 <= room);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [area, nav, menu]);

  return {
    fits,
    setArea,
    setNav,
    menuRef: setMenu,
    navClass: NAV_FROM[from],
    menuClass: fits ? MENU_HIDDEN_FROM[from] : "",
  };
};

/**
 * The slot between the logo and the right cluster. `overflow-hidden` + `relative`
 * keep the invisible measuring copy of a too-wide nav from widening the page.
 * `align`: "center" for the split layout, "end" to sit beside Book Now.
 */
export const NavSlot: FC<{ fit: NavFit; align: "center" | "end"; className?: string }> = ({
  fit,
  align,
  className = "",
}) => (
  <div
    ref={fit.setArea}
    className={`relative flex min-w-0 flex-1 items-center overflow-hidden ${
      align === "center" ? "justify-center" : "justify-end"
    } ${className}`}
  >
    <nav
      ref={fit.setNav}
      className={`${fit.navClass} w-max flex-shrink-0 items-center ${fit.fits ? "" : "invisible absolute left-0"}`}
    >
      <Navigation />
    </nav>
  </div>
);
